import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  Box, Paper, Typography, Button, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Switch, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, FormControl, InputLabel,
  Select, MenuItem, Alert, IconButton, Tooltip, CircularProgress
} from "@mui/material";
import { Add, Edit, Delete, PlayArrow } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAutomations, useMarketingSettings, usePresetPreview, DAY_NAMES } from "../Campaign/hooks/useCampaign";
import { countSmsParts, previewSms, formatBdt } from "../Campaign/utils/smsParts";
import { presetOptionText } from "../AICalling/useAICalling";
import "../Campaign/Campaign.css";

const blank = {
  name: "",
  enabled: false,
  preset: { key: "abandonedCheckout", options: {} },
  type: "sms",
  smsMessage: { content: "", landing: "checkoutProfile" },
  pushMessage: { title: "", body: "" },
  slot: "21:00",
  days: [3, 0],
  holdoutPercent: 10,
  attributionDays: 7,
  cooldownDays: 7,
  slots: [],
  offer: { enabled: false, minAmount: 500, bonusPercent: 100, maxBonus: 500, validHours: 24 }
};

const APP = "https://app.bibahobangla.com";
// Where a tap on the push opens (app link / web link); "wallet" opens Add Money at the offer's minimum
const PUSH_TARGETS = {
  dashboard: () => ({ redirectUrlMobile: "bibahobangla://dashboard", redirectUrlWeb: `${APP}/dashboard` }),
  wallet: (amount) => ({ redirectUrlMobile: `bibahobangla://Wallet?addMoney=1&amount=${amount}`, redirectUrlWeb: `${APP}/wallet?addMoney=1&amount=${amount}` }),
  packages: () => ({ redirectUrlMobile: "bibahobangla://profile?openPackages=1", redirectUrlWeb: `${APP}/my-profile?openPackages=1` })
};
const pushTargetOf = (url = "") => (/wallet/i.test(url) ? "wallet" : /openPackages/.test(url) ? "packages" : "dashboard");
const HOURLY = Array.from({ length: 12 }, (_, i) => `${String(10 + i).padStart(2, "0")}:00`); // 10:00–21:00
const offerBonus = (o, amount) => {
  if (!o?.enabled || !(amount >= Number(o.minAmount))) return 0;
  const bonus = Math.floor((amount * Number(o.bonusPercent)) / 100);
  return Number(o.maxBonus) > 0 ? Math.min(bonus, Number(o.maxBonus)) : bonus;
};

const dhaka = (d) => (d ? new Date(d).toLocaleString("en-GB", { timeZone: "Asia/Dhaka", dateStyle: "medium", timeStyle: "short" }) : "—");

/** Automatic campaigns from audience presets (plan item 31) */
const Automations = () => {
  const navigate = useNavigate();
  const { automations, featureOn, loading, fetch, create, update, remove, run } = useAutomations();
  const { settings, presets, permissions, fetch: fetchSettings } = useMarketingSettings();
  const { preview, loading: previewLoading, fetch: fetchPreview } = usePresetPreview();
  const [editing, setEditing] = useState(null); // automation being edited (or blank)
  const [busy, setBusy] = useState(false);

  useEffect(() => { fetch(); fetchSettings(); }, []);
  useEffect(() => {
    if (!editing) return;
    const t = setTimeout(() => fetchPreview(editing.preset?.key, editing.preset?.options), 400);
    return () => clearTimeout(t);
  }, [editing?.preset?.key, JSON.stringify(editing?.preset?.options)]);

  const canEdit = permissions.canEdit;
  const setE = (path, value) =>
    setEditing((e) => {
      const next = structuredClone(e);
      const keys = path.split(".");
      let o = next;
      keys.slice(0, -1).forEach((k) => { o[k] = o[k] ?? {}; o = o[k]; });
      o[keys[keys.length - 1]] = value;
      return next;
    });

  const submit = async () => {
    setBusy(true);
    const { _id, createdAt, updatedAt, lastRunDate, lastRunKey, lastRunAt, lastCampaignId, lastResult, runs, createdBy, createdByName, offerStats, __v, ...automation } = editing;
    if (automation.type !== "sms") {
      const target = pushTargetOf(automation.pushMessage?.redirectUrlMobile);
      automation.pushMessage = { ...automation.pushMessage, ...PUSH_TARGETS[target](Number(automation.offer?.minAmount) || 500) };
    }
    const res = _id ? await update(_id, automation) : await create(automation);
    setBusy(false);
    if (res.success) setEditing(null);
  };

  const runNow = async (a) => {
    if (!window.confirm(`Send "${a.name}" now to everyone in its audience? This sends real messages.`)) return;
    setBusy(true);
    const res = await run(a._id);
    setBusy(false);
    if (res.success && res.data?.campaignId) navigate(`/campaign/${res.data.campaignId}`);
  };

  const presetInfo = presets.find((p) => p.key === editing?.preset?.key);
  const sms = editing ? countSmsParts(previewSms(editing.smsMessage?.content || "", settings?.shortLinkStyle)) : null;

  return (
    <Box className="campaign-container">
      <ToastContainer position="top-right" autoClose={3000} />
      <Box className="campaign-header">
        <Typography variant="h5">Automations</Typography>
        <Button variant="contained" startIcon={<Add />} disabled={!canEdit} onClick={() => setEditing(structuredClone(blank))}>New automation</Button>
      </Box>

      {!featureOn && (
        <Alert severity="info" sx={{ mb: 2 }}>
          "Automatic daily sends" is off in Marketing settings → Features, so automations don't run on their own. "Run now" still works.
        </Alert>
      )}

      <Paper className="form-section">
        {loading ? <CircularProgress /> : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>On</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Audience</TableCell>
                  <TableCell>Channel</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell>Last run</TableCell>
                  <TableCell align="right">Runs</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {automations.length === 0 && <TableRow><TableCell colSpan={8}>No automations yet.</TableCell></TableRow>}
                {automations.map((a) => (
                  <TableRow key={a._id} hover>
                    <TableCell>
                      <Switch size="small" checked={!!a.enabled} disabled={!canEdit || busy} onChange={(e) => update(a._id, { enabled: e.target.checked })} />
                    </TableCell>
                    <TableCell>
                      {a.name}
                      {a.offer?.enabled && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Offer: pay ৳{a.offer.minAmount} → +{a.offer.bonusPercent}%{a.offer.maxBonus > 0 ? ` (max ৳${a.offer.maxBonus})` : ""}, {a.offer.validHours} h
                        </Typography>
                      )}
                      {a.offerStats && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {a.offerStats.given} given · {a.offerStats.viewed} seen · {a.offerStats.started} started · {a.offerStats.used} used · paid ৳{a.offerStats.paid} · bonus ৳{a.offerStats.bonus}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{presets.find((p) => p.key === a.preset?.key)?.label || a.preset?.key}</TableCell>
                    <TableCell><Chip size="small" label={a.type.toUpperCase()} /></TableCell>
                    <TableCell>
                      {a.slots?.length ? [...new Set([a.slot, ...a.slots])].sort().join(", ") : a.slot} · {(a.days || []).map((d) => DAY_NAMES[d]).join(", ")}
                    </TableCell>
                    <TableCell>
                      {dhaka(a.lastRunAt)}
                      {a.lastCampaignId && <Button size="small" onClick={() => navigate(`/campaign/${a.lastCampaignId}`)}>Results</Button>}
                      {a.lastResult && <Typography variant="caption" display="block" color="text.secondary">{a.lastResult}</Typography>}
                    </TableCell>
                    <TableCell align="right">{a.runs || 0}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      <Tooltip title="Run now"><span><IconButton size="small" disabled={!canEdit || busy} onClick={() => runNow(a)}><PlayArrow /></IconButton></span></Tooltip>
                      <Tooltip title="Edit"><span><IconButton size="small" disabled={!canEdit} onClick={() => setEditing(structuredClone(a))}><Edit /></IconButton></span></Tooltip>
                      <Tooltip title="Delete"><span><IconButton size="small" disabled={!canEdit} onClick={() => window.confirm(`Delete "${a.name}"?`) && remove(a._id)}><Delete /></IconButton></span></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Each run is a normal campaign with its own results page. Members who got this automation in the last cooldown days are left out, and the usual SMS limits, quiet hours and opt-outs apply.
        </Typography>
      </Paper>

      <Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="md" fullWidth>
        <DialogTitle>{editing?._id ? "Edit automation" : "New automation"}</DialogTitle>
        {editing && (
          <DialogContent dividers>
            <TextField label="Name" fullWidth margin="dense" value={editing.name} onChange={(e) => setE("name", e.target.value)} />
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <FormControl margin="dense" sx={{ flex: "1 1 240px" }}>
                <InputLabel>Audience</InputLabel>
                <Select label="Audience" value={editing.preset.key} onChange={(e) => setE("preset", { key: e.target.value, options: {} })}>
                  {presets.map((p) => <MenuItem key={p.key} value={p.key}>{p.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl margin="dense" sx={{ flex: "1 1 140px" }}>
                <InputLabel>Channel</InputLabel>
                <Select label="Channel" value={editing.type} onChange={(e) => setE("type", e.target.value)}>
                  <MenuItem value="sms">SMS</MenuItem>
                  <MenuItem value="push">Push</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {presetInfo && <Typography variant="body2" color="text.secondary">{presetInfo.description}.</Typography>}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              {Object.keys(presetInfo?.options || {}).map((k) => (
                <TextField key={k} type="number" size="small" margin="dense" label={presetOptionText(editing.preset.key, k).label} sx={{ width: 200 }}
                  helperText={presetOptionText(editing.preset.key, k).help}
                  value={editing.preset.options?.[k] ?? presetInfo.options[k]}
                  onChange={(e) => setE(`preset.options.${k}`, e.target.value === "" ? undefined : Number(e.target.value))} />
              ))}
            </Box>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {previewLoading ? "Counting…" : preview ? `Right now: ${preview.count} members (${preview.withMobile} with a number, ${preview.withPush} with push)` : ""}
            </Typography>

            {editing.type !== "push" && (
              <>
                <TextField label="SMS text" fullWidth multiline rows={3} margin="dense" inputProps={{ maxLength: 160 }}
                  value={editing.smsMessage.content} onChange={(e) => setE("smsMessage.content", e.target.value)}
                  helperText={`Use {TrackingLink}. ${editing.smsMessage.content.length}/160 · ${sms.parts} part${sms.parts === 1 ? "" : "s"} · ${formatBdt(sms.parts * (settings?.costs?.smsRatePerPart ?? 0.35))} per person`} />
                <FormControl margin="dense" fullWidth>
                  <InputLabel>Link opens</InputLabel>
                  <Select label="Link opens" value={editing.smsMessage.landing} onChange={(e) => setE("smsMessage.landing", e.target.value)}>
                    <MenuItem value="checkoutProfile">The profile they tried to unlock</MenuItem>
                    <MenuItem value="packages">Packages</MenuItem>
                    <MenuItem value="dashboard">Dashboard</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}
            {editing.type !== "sms" && (
              <>
                <TextField label="Push title" fullWidth margin="dense" value={editing.pushMessage?.title || ""} onChange={(e) => setE("pushMessage.title", e.target.value)} />
                <TextField label="Push text" fullWidth multiline rows={2} margin="dense" value={editing.pushMessage?.body || ""} onChange={(e) => setE("pushMessage.body", e.target.value)} />
                <FormControl margin="dense" fullWidth>
                  <InputLabel>Push opens</InputLabel>
                  <Select label="Push opens" value={pushTargetOf(editing.pushMessage?.redirectUrlMobile)}
                    onChange={(e) => setE("pushMessage", { ...editing.pushMessage, ...PUSH_TARGETS[e.target.value](Number(editing.offer?.minAmount) || 500) })}>
                    <MenuItem value="dashboard">Dashboard</MenuItem>
                    <MenuItem value="wallet">Wallet with Add Money (amount = offer minimum)</MenuItem>
                    <MenuItem value="packages">Packages</MenuItem>
                  </Select>
                </FormControl>
              </>
            )}

            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mt: 1 }}>
              <TextField type="time" label="Send at" InputLabelProps={{ shrink: true }} margin="dense" value={editing.slot} onChange={(e) => setE("slot", e.target.value)} />
              <TextField label="More send times (Bangladesh)" margin="dense" sx={{ flex: "1 1 260px" }} placeholder="10:00, 11:00, 12:00"
                value={(editing.slots || []).join(", ")}
                onChange={(e) => setE("slots", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))}
                helperText="Each time runs once a day. Leave empty for one send a day." />
              <Button size="small" onClick={() => { setE("slot", "10:00"); setE("slots", HOURLY); }}>Every hour 10:00–21:00</Button>
              {DAY_NAMES.map((d, i) => {
                const on = editing.days.includes(i);
                return <Chip key={d} label={d} color={on ? "primary" : "default"} variant={on ? "filled" : "outlined"}
                  onClick={() => setE("days", on ? editing.days.filter((x) => x !== i) : [...editing.days, i])} />;
              })}
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField type="number" label="Holdout (%)" margin="dense" sx={{ width: 150 }} value={editing.holdoutPercent} onChange={(e) => setE("holdoutPercent", e.target.value)} />
              <TextField type="number" label="Count payments within (days)" margin="dense" sx={{ width: 220 }} value={editing.attributionDays} onChange={(e) => setE("attributionDays", e.target.value)} />
              <TextField type="number" label="Don't repeat to a member within (days)" margin="dense" sx={{ width: 260 }} value={editing.cooldownDays} onChange={(e) => setE("cooldownDays", e.target.value)} />
            </Box>

            <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Switch checked={!!editing.offer?.enabled} onChange={(e) => setE("offer.enabled", e.target.checked)} />
                <Typography variant="subtitle2">Give each member a wallet top-up offer (no code)</Typography>
              </Box>
              {editing.offer?.enabled && (
                <>
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    <TextField type="number" label="Minimum top-up (৳)" margin="dense" sx={{ width: 170 }} value={editing.offer.minAmount} onChange={(e) => setE("offer.minAmount", Number(e.target.value))} />
                    <TextField type="number" label="Bonus (%)" margin="dense" sx={{ width: 120 }} value={editing.offer.bonusPercent} onChange={(e) => setE("offer.bonusPercent", Number(e.target.value))} />
                    <TextField type="number" label="Maximum bonus (৳, 0 = none)" margin="dense" sx={{ width: 210 }} value={editing.offer.maxBonus} onChange={(e) => setE("offer.maxBonus", Number(e.target.value))} />
                    <TextField type="number" label="Valid for (hours)" margin="dense" sx={{ width: 150 }} value={editing.offer.validHours} onChange={(e) => setE("offer.validHours", Number(e.target.value))} />
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Pay ৳{editing.offer.minAmount} → wallet gets ৳{Number(editing.offer.minAmount) + offerBonus(editing.offer, Number(editing.offer.minAmount))}.
                    Once per member, given just before the push; held-out members don't get it. A coupon typed by the member is used instead.
                  </Typography>
                </>
              )}
            </Paper>
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" disabled={busy} onClick={submit}>{busy ? "Saving…" : "Save"}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Automations;
